import { StatsType } from '@/api/gql/graphql';
import type { UserStatsOutput } from '@/api/gql/graphql';
import { useUserStats } from '@/api/hooks/useStats';
import { useUserStatistics } from '@/api/hooks/user/useUserStatistics';
import { useMemo, useState } from 'react';
import user from '@/assets/icons/user.svg';
import userDeleted from '@/assets/icons/user-deleted.svg';
import { useCustomFilterStore } from '@/store/CustomFilterStore/CustomFilter';
import { useFormatDate } from '@/hooks/useFormatDate';

export const useDashboard = () => {
  const { formatDateUs } = useFormatDate();
  const { selectedDateRange, isFilterApplied } = useCustomFilterStore();

  const statistics = isFilterApplied
    ? {
        from: selectedDateRange ? formatDateUs(selectedDateRange[0]) : null,
        to: selectedDateRange ? formatDateUs(selectedDateRange[1]) : null,
      }
    : { from: null, to: null };

  const getStatsOptions = (type: StatsType) => {
    return {
      type,
      statistics,
    };
  };

  const { data: userStatistics } = useUserStatistics({
    from: statistics.from || null,
    to: statistics.to || null,
  });

  const { data: subscriptionData } = useUserStats(
    getStatsOptions(StatsType.Subscription),
  );

  const { data: regionData } = useUserStats(getStatsOptions(StatsType.Region));
  const { data: classData } = useUserStats(getStatsOptions(StatsType.Class));

  const { data: genderData } = useUserStats(getStatsOptions(StatsType.Gender));

  const { data: candidateStatusData } = useUserStats(
    getStatsOptions(StatsType.CandidateStatus),
  );

  const { data: seriesData } = useUserStats(getStatsOptions(StatsType.Series));

  const [selectedType, setSelectedType] = useState('offre');

  const filterOptions = useMemo(
    () => [
      { name: 'Offre', _id: 'offre' },
      { name: 'Genre', _id: 'genre' },
      { name: 'Classe', _id: 'classe' },
      { name: 'Candidat', _id: 'status' },
    ],
    [],
  );
  const statisticsData = useMemo<UserStatsOutput | undefined>(() => {
    switch (selectedType) {
      case 'offre':
        return subscriptionData;
      case 'genre':
        return genderData;
      case 'classe':
        return classData;
      case 'status':
        return candidateStatusData;
      default:
        return subscriptionData;
    }
  }, [
    selectedType,
    subscriptionData,
    genderData,
    classData,
    candidateStatusData,
  ]);

  const cards = useMemo(
    () => [
      {
        title: 'Utilisateurs Total',
        value: userStatistics?.totalUsers,
        icon: user,
      },
      {
        title: 'Utilisateurs supprimés',
        value: userStatistics?.totalUsersDeleted,
        icon: userDeleted,
        valueColor: 'text-[#ED544E]',
      },
      {
        icon: user,
        title: 'Abonnement premium',
        value:
          (subscriptionData?.values[1] || 0) +
          (subscriptionData?.values[2] || 0),
      },
    ],
    [userStatistics, subscriptionData],
  );

  const { title, description } = useMemo(() => {
    const titleMap = {
      offre: { title: 'Abonnement', description: 'Nombre d’abonnés par offre' },
      genre: { title: 'Genre', description: 'Répartition par genre' },
      classe: { title: 'Classe', description: 'Nombre d’élèves par classe' },
      status: {
        title: 'Statut',
        description: 'Répartition par status de candidature',
      },
    };
    return titleMap[selectedType as keyof typeof titleMap] || titleMap['offre'];
  }, [selectedType]);

  const colors = useMemo(() => ['#FEC200', '#FF5500', '#FFFFFF'], []);

  const percentage = useMemo(() => {
    const totalValue =
      statisticsData?.values?.reduce(
        (sum: number, item: number) => sum + item,
        0,
      ) ?? 0;
    return statisticsData?.values?.map((item: number, index: number) => ({
      color: colors[index % colors.length],
      label: statisticsData.labels[index],
      value: item,
      percentage:
        totalValue > 0
          ? Number(((item / totalValue) * 100).toFixed(1))
          : 0,
    }));
  }, [statisticsData, colors]);

  return {
    userStatistics,
    subscriptionData,
    regionData,
    classData,
    genderData,
    candidateStatusData,
    seriesData,
    selectedType,
    setSelectedType,
    statisticsData,
    cards,
    filterOptions,
    title,
    description,
    percentage,
    colors,
    selectedDateRange,
  };
};
